import React, { useEffect, useState } from 'react'
import MainTemplate from './components/MainTemplate/MainTemplate'
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import checkStatus from './jsFunctions/checkStatus.js';
import { getCampusSessions } from './utils/campusPaymentCharges.js';
import { getCampusPaymentSummary, hasOutstandingCampusPayment, isFamilyStatusComplete } from './utils/campusPaymentStatus.js';
import { buildPaymentResumeNavState } from './utils/paymentResume.js';
import { storeCampusSessions } from './utils/campusPaymentCharges.js';
import { fetchWithdrawnChildren, reEnrolWithdrawnChildren } from './api/withdrawnChild.js';
import { formatMoney } from './utils/money';


const RenrolCompleted = () => {
    const { state = {} } = useLocation();
    const navigate = useNavigate();
    const { ParentID: authParentID } = useSelector((store) => store.auth);

    /**
     * The family's parent code, from wherever it survived.
     *
     * Not every route here carries it: Login's resume path passes `navState?.ParentID`, which is
     * empty when the stored navState has gone, and this screen is also reached from Thankyou,
     * PaymentResponse and BpointPaymentForm. checkStatus has always fallen back to localStorage for
     * the same reason; anything asking about the family has to do the same or it silently asks about
     * nobody.
     */
    const parentCode = String(
        state.ParentID
        || authParentID
        || (JSON.parse(localStorage.getItem('navState') || 'null') || {}).ParentID
        || ''
    ).trim();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [verificationError, setVerificationError] = useState('');

    useEffect(() => {
        if (localStorage.getItem("LoginToken") === null) {
            navigate('/');
            return;
        }

        const verifyFamilyCompletion = async () => {
            const result = await checkStatus({ campusSessions: getCampusSessions() });
            if (!result) {
                setVerificationError('We could not confirm your submission status. Please refresh this page or contact the office.');
                setLoading(false);
                return;
            }
            if (!isFamilyStatusComplete(result) && (!result?.allComplete || hasOutstandingCampusPayment(result))) {
                // Same fallback: resuming a payment must not lose the family either.
                const resume = buildPaymentResumeNavState({ statusData: result, ParentID: parentCode });
                if (resume) {
                    navigate('/payment', { replace: true, state: resume });
                    return;
                }

                const { unpaidCampuses } = getCampusPaymentSummary(result);
                const owing = unpaidCampuses.map((campus) => campus.college).filter(Boolean).join(', ');
                setVerificationError(
                    owing
                        ? `Payment is still outstanding for ${owing}. Continue to the payment page to pay the remaining charges.`
                        : 'One or more campus payments are still outstanding. Continue to the payment page to pay the remaining charges.'
                );
            }
            setStatus(result);
            setLoading(false);
        };

        verifyFamilyCompletion();
    }, [navigate]);

    // Children this family marked as leaving, if they are allowed to change their mind. The server
    // decides: an administrator has to have turned it on, and a campus holding credit is always
    // sent to the accounts team instead. Asking rather than working it out here keeps the screen
    // and the rule from disagreeing.
    const [canReEnrol, setCanReEnrol] = useState(null);
    const [reEnrolling, setReEnrolling] = useState(false);
    const [reEnrolError, setReEnrolError] = useState('');

    useEffect(() => {
        if (!parentCode || loading || verificationError) return;

        let cancelled = false;
        fetchWithdrawnChildren(parentCode)
            .then((data) => { if (!cancelled) setCanReEnrol(data); })
            // Never worth a message of its own: the page still says the re-enrolment is complete,
            // and the line below simply does not appear.
            .catch(() => { if (!cancelled) setCanReEnrol(null); });

        return () => { cancelled = true; };
    }, [parentCode, loading, verificationError]);

    const startAgainFor = async (studCodes) => {
        setReEnrolling(true);
        setReEnrolError('');

        try {
            const data = await reEnrolWithdrawnChildren(parentCode, studCodes);
            const sessions = data.campusSessions || [];
            if (!sessions.length) throw new Error('nothing started');

            // The new re-enrolment is its own session with its own payment, so the journey starts
            // from the beginning rather than resuming the one that is already paid.
            storeCampusSessions(sessions);
            localStorage.setItem('LogID', String(data.SessionID));

            const navState = JSON.parse(localStorage.getItem('navState') || 'null') || {};
            navigate('/introduction', {
                state: { ...navState, ParentID: parentCode, SessionID: data.SessionID },
            });
        } catch (error) {
            setReEnrolError(
                error?.response?.data?.error
                || 'We could not start that. Please contact the school’s Accounts Receivable team.'
            );
            setReEnrolling(false);
        }
    };

    const campusStatuses = status?.campusStatuses || state.campusStatuses || [];
    // A child marked as leaving is the one thing a family cannot undo here: one re-enrolment holds
    // one payment, so a second child cannot be added without overwriting the first receipt. They
    // are told who to ask rather than left to find nothing.
    const notReturningCount = Number(status?.notReturningCount ?? state.notReturningCount ?? 0) || 0;
    const completedDate = status?.t_date || state.t_date;
    const completedTime = status?.t_time || state.t_time;
    const college = campusStatuses[0]?.college || state.college || 'Kewdale';
    const familyName = campusStatuses.find((campus) => campus.family_name)?.family_name || '';
    const familyLine = [parentCode ? `Parent code ${parentCode}` : '', familyName].filter(Boolean).join(' · ');

    const content = loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: '#475569' }}>
                Confirming all campus enrolments…
            </Typography>
        </Box>
    ) : verificationError ? (
        <Card sx={{ maxWidth: 720, mx: 'auto', my: 4, p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 'none' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                Submission not yet confirmed
            </Typography>
            <Typography sx={{ color: '#475569' }}>{verificationError}</Typography>
            <Button
                variant="contained"
                sx={{ mt: 3, bgcolor: '#059669', textTransform: 'none', fontWeight: 700, borderRadius: '12px', px: 3, py: 1.25, '&:hover': { bgcolor: '#047857' } }}
                onClick={() => {
                    const navState = JSON.parse(localStorage.getItem('navState') || 'null');
                    navigate('/introduction', { state: { ...(navState || {}), resumePayment: true } });
                }}
            >
                Continue to payment
            </Button>
        </Card>
    ) : (
        <Box sx={{ maxWidth: 720, mx: 'auto', py: 3 }}>
            <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', borderRadius: 3, border: '1px solid #bbf7d0', boxShadow: 'none' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#059669', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                    Re-enrolment submitted
                </Typography>
                <Typography sx={{ color: '#475569', mb: familyLine ? 1 : 3 }}>
                    All campus enrolments and required payments for your family have been completed successfully.
                </Typography>
                {familyLine && (
                    <Typography sx={{ color: '#64748b', fontSize: 14, mb: 3 }}>{familyLine}</Typography>
                )}

                {/* The payment, per campus, read from the server — so it is the same whether the
                    family has just paid or comes back next week. This replaced a separate screen
                    of transaction details that was shown for a moment before this one. */}
                {campusStatuses.map((campus) => (
                    <CampusPayment key={campus.SessionID || campus.college} campus={campus} />
                ))}

                {campusStatuses.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, pt: 1, mb: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Total paid</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {formatMoney(campusStatuses.reduce((total, campus) => total + (Number(campus.paid_amount) || 0), 0), { blankWhenMissing: false })}
                        </Typography>
                    </Box>
                )}

                {/*
                  Offered only when the server says so. While it is switched off, or the campus
                  holds credit, the family is asked to ring the accounts team instead — the
                  message below.
                */}
                {canReEnrol?.allowed && canReEnrol.children?.length > 0 && (
                    <Box sx={{ mt: 3, p: 2, textAlign: 'left', bgcolor: '#f0f9ff', border: '1px solid #7dd3fc', borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, color: '#075985', fontSize: 14, mb: 0.5 }}>
                            Changed your mind?
                        </Typography>
                        <Typography sx={{ color: '#0c4a6e', fontSize: 14, mb: 1.5 }}>
                            {canReEnrol.children.length === 1
                                ? `You told us ${canReEnrol.children[0].first_name} will not be returning. You can still re-enrol them — it is a separate re-enrolment, with its own payment.`
                                : 'You told us some of your children will not be returning. You can still re-enrol them — each is a separate re-enrolment, with its own payment.'}
                        </Typography>

                        {reEnrolError && (
                            <Typography sx={{ color: '#b91c1c', fontSize: 13, mb: 1.5 }}>
                                {reEnrolError}
                            </Typography>
                        )}

                        {canReEnrol.children.map((child) => (
                            <Button
                                key={child.stud_code}
                                variant="outlined"
                                disabled={reEnrolling}
                                sx={{ mr: 1, mb: 1, textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                                onClick={() => startAgainFor([child.stud_code])}
                            >
                                {reEnrolling
                                    ? 'Starting…'
                                    : `Re-enrol ${child.first_name} ${child.surname}`.trim()}
                            </Button>
                        ))}
                    </Box>
                )}

                {!canReEnrol?.allowed && notReturningCount > 0 && (
                    <Box sx={{ mt: 3, p: 2, textAlign: 'left', bgcolor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, color: '#92400e', fontSize: 14, mb: 0.5 }}>
                            {notReturningCount === 1
                                ? 'You told us one child will not be returning'
                                : `You told us ${notReturningCount} children will not be returning`}
                        </Typography>
                        <Typography sx={{ color: '#78350f', fontSize: 14 }}>
                            {`If your plans have changed and you would now like to re-enrol ${notReturningCount === 1 ? 'them' : 'any of them'}, please contact the school's Accounts Receivable team. They can complete it for you — it cannot be done on this page once a payment has been made.`}
                        </Typography>
                    </Box>
                )}

                {(completedDate || completedTime) && (
                    <Typography sx={{ mt: 3, fontSize: 14, color: '#64748b' }}>
                        Submitted {completedDate || ''}{completedDate && completedTime ? ' at ' : ''}{completedTime || ''}
                    </Typography>
                )}
            </Card>
        </Box>
    );

    return (
        <MainTemplate college={`${college}`} content={content} />
    )
}

/**
 * One campus's payment: how much, how it was paid, and the receipt to quote if the family calls.
 * A payment made entirely from credit has no card receipt, so none is shown for it.
 */
export const CampusPayment = ({ campus }) => {
    const card = Number(campus.AmountPaidFromBpoint) || 0;
    const credit = Number(campus.AmountPaidFromAICCredit) || 0;
    const paid = Number(campus.paid_amount) || card + credit;
    const receipt = Number(campus.t_receiptnum) > 0 ? String(campus.t_receiptnum) : '';
    const how = [
        card > 0 ? `${formatMoney(card)} by card` : '',
        credit > 0 ? `${formatMoney(credit)} from credit` : '',
    ].filter(Boolean).join(' · ');

    return (
        <Box sx={{ p: 1.5, mb: 1, bgcolor: '#f0fdf4', borderRadius: 2, textAlign: 'left' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontWeight: 600, color: '#334155' }}>{campus.college}</Typography>
                <Typography sx={{ fontWeight: 700, color: '#047857' }}>
                    {paid > 0 ? `${formatMoney(paid)} paid` : 'Payment received'}
                </Typography>
            </Box>
            {(how || receipt) && (
                <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.5 }}>
                    {[how, receipt ? `Receipt ${receipt}` : ''].filter(Boolean).join(' · ')}
                </Typography>
            )}
        </Box>
    );
};

export default RenrolCompleted
